import {Button, Group, SegmentedControl, Select, Stack, Title} from '@mantine/core'
import {IconMap, IconPackage, IconReport, IconSend} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'
import {forwardRef, type ReactNode} from 'react'

import type {DispatchYard} from '../dispatch/schemas'
import {ALL_YARDS} from '../dispatch/yards'
import type {BoardLayout} from './board-layout'
import {BoardLayoutPicker} from './board-layout-picker'
import classes from './dispatch-timeline-header.module.css'
import {TimeNavigation} from './time-navigation'
import type {ViewRange} from './types'

type DispatchTimelineHeaderProps = {
    viewRange: ViewRange
    onViewRangeChange: (value: ViewRange) => void
    anchorOffset: number
    onAnchorOffsetChange: (value: number) => void
    boardLayout: BoardLayout
    onBoardLayoutChange: (value: BoardLayout) => void
    isMobileViewport?: boolean
    belowTitle?: ReactNode
    customerId?: string | null
    customerOptions?: {value: string; label: string}[]
    customerOptionsLoading?: boolean
    onCustomerIdChange?: (customerId: string | null) => void
    yards?: DispatchYard[]
    yardId?: string | null
    onYardChange?: (yardId: string | null) => void
    onPublish?: () => void
    mapView?: boolean
    onMapViewChange?: (value: boolean) => void
    rentalsView?: boolean
    onRentalsViewChange?: (value: boolean) => void
}

export const DispatchTimelineHeader = forwardRef<HTMLDivElement, DispatchTimelineHeaderProps>(
    function DispatchTimelineHeader(
        {
            viewRange,
            onViewRangeChange,
            anchorOffset,
            onAnchorOffsetChange,
            boardLayout,
            onBoardLayoutChange,
            isMobileViewport = false,
            belowTitle,
            customerId = null,
            customerOptions = [],
            customerOptionsLoading = false,
            onCustomerIdChange,
            yards = [],
            yardId = null,
            onYardChange,
            onPublish,
            mapView = false,
            onMapViewChange,
            rentalsView = false,
            onRentalsViewChange,
        },
        ref,
    ) {
        return (
            <div ref={ref} className={classes.root}>
                <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
                    <Stack gap="xs" className={classes.titleColumn}>
                        <Title order={1} className={classes.title}>
                            Dispatch board
                        </Title>
                        {yards.length > 0 && onYardChange && (
                            <SegmentedControl
                                aria-label="Yard board"
                                data={[
                                    ...yards.map((yard) => ({value: yard.id, label: yard.name})),
                                    {value: ALL_YARDS, label: 'All yards'},
                                ]}
                                value={yardId ?? ALL_YARDS}
                                onChange={(value) =>
                                    onYardChange(value === ALL_YARDS ? null : value)
                                }
                                data-testid="yard-switch"
                            />
                        )}
                        {belowTitle}
                    </Stack>
                    <Group gap="sm" wrap="wrap" className={classes.toolbar}>
                        {onPublish ? (
                            <Button
                                onClick={onPublish}
                                leftSection={<IconSend size={16} stroke={1.5} />}
                            >
                                Publish
                            </Button>
                        ) : null}
                        <Button
                            component={Link}
                            to="/dispatch/report"
                            variant="default"
                            leftSection={<IconReport size={16} stroke={1.5} />}
                        >
                            Dispatch report
                        </Button>
                        {!isMobileViewport && (
                            <BoardLayoutPicker
                                value={boardLayout}
                                onChange={(value) => {
                                    onMapViewChange?.(false)
                                    onRentalsViewChange?.(false)
                                    onBoardLayoutChange(value)
                                }}
                            />
                        )}
                        {onMapViewChange ? (
                            <Button
                                variant={mapView ? 'light' : 'default'}
                                aria-pressed={mapView}
                                leftSection={<IconMap size={16} stroke={1.5} />}
                                onClick={() => {
                                    onRentalsViewChange?.(false)
                                    onMapViewChange(!mapView)
                                }}
                            >
                                Map
                            </Button>
                        ) : null}
                        {onRentalsViewChange ? (
                            <Button
                                variant={rentalsView ? 'light' : 'default'}
                                aria-pressed={rentalsView}
                                leftSection={<IconPackage size={16} stroke={1.5} />}
                                onClick={() => {
                                    onMapViewChange?.(false)
                                    onRentalsViewChange(!rentalsView)
                                }}
                            >
                                Active rentals
                            </Button>
                        ) : null}
                        <Select
                            aria-label="Filter by customer"
                            placeholder={
                                customerOptionsLoading ? 'Loading customers…' : 'All customers'
                            }
                            data={customerOptions}
                            value={customerId}
                            onChange={onCustomerIdChange}
                            searchable
                            clearable
                            clearButtonProps={{
                                'aria-hidden': false,
                                'aria-label': 'Clear customer filter',
                            }}
                            nothingFoundMessage="No customers found"
                            w={220}
                        />
                        <TimeNavigation
                            viewRange={viewRange}
                            onViewRangeChange={onViewRangeChange}
                            anchorOffset={anchorOffset}
                            onAnchorOffsetChange={onAnchorOffsetChange}
                        />
                    </Group>
                </Group>
            </div>
        )
    },
)
